import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AnalyticsTab } from "./tabs/AnalyticsTab.tsx";
import { GovernanceTab } from "./tabs/GovernanceTab.tsx";
import { FinanceTab } from "./tabs/FinanceTab.tsx";
import { MonitorTab } from "./tabs/MonitorTab.tsx";
import { LoginGate, useIdentity } from "./components/LoginGate.tsx";
import { SubmitProposalPage } from "./tabs/SubmitProposalPage.tsx";
import { MMPortal } from "./tabs/MMPortal.tsx";
import { BottomNav, type NavIcon } from "./components/BottomNav.tsx";
import { useIsMobile } from "./lib/useIsMobile.ts";
import { logout } from "./api/auth.ts";

// Top-level shell for the dashboard that replaces the legacy Dash portal.
// Session-gated (increment 2): operators see everything; client sessions are
// server-side scoped to their own data — the shell just reflects that.
// Governance is the advisory / RFP side; Finance (operator only) is the borrow / lend side —
// deals, the network and the counterparty registry — split out of Governance so each business
// line has its own front door. They share the entity registry underneath.
// Phone widths: the tab row leaves the top bar and becomes a bottom tab bar (BottomNav); the
// top bar keeps the brand and sign-out. Desktop is unchanged.
type Tab = "analytics" | "governance" | "finance" | "compliance";

const TABS: { id: Tab; label: string; icon: NavIcon; ready: boolean; operatorOnly?: boolean }[] = [
  { id: "analytics", label: "Analytics", icon: "analytics", ready: true },
  { id: "governance", label: "Governance", icon: "governance", ready: true },
  { id: "compliance", label: "Compliance", icon: "compliance", ready: true },
  { id: "finance", label: "Finance", icon: "finance", ready: true, operatorOnly: true },
];

export function App() {
  // LIQ-64: MM submission links (/rfp/<token>) are token-authenticated —
  // the token is the credential; they must not sit behind the LoginGate.
  // Counterparty portal (borrowers + lenders): /portal/*. Legacy /mm/* links redirect.
  const legacyMm = window.location.pathname.match(/^\/mm(?:\/(.*))?$/);
  if (legacyMm) {
    window.location.replace(`/portal${legacyMm[1] ? "/" + legacyMm[1] : ""}${window.location.search}`);
    return null;
  }
  const portalMatch = window.location.pathname.match(/^\/portal(?:\/(.*))?$/);
  if (portalMatch) {
    return <MMPortal subpath={portalMatch[1] || ""} />;
  }
  const rfpMatch = window.location.pathname.match(/^\/rfp\/([^/]+)/);
  if (rfpMatch) {
    return <SubmitProposalPage token={decodeURIComponent(rfpMatch[1])} />;
  }
  return (
    <LoginGate>
      <Shell />
    </LoginGate>
  );
}

function Shell() {
  const identity = useIdentity();
  const queryClient = useQueryClient();
  const mobile = useIsMobile();
  const [tab, setTab] = useState<Tab>("analytics");
  const visible = TABS.filter((t) => !t.operatorOnly || identity.role === "operator");

  const signOut = () => {
    logout()
      .catch(() => {})
      .finally(() => {
        // Drop ALL cached data, then hard-reload: clear() does not notify
        // mounted queries (whoami never refetched — sign-out appeared dead),
        // and a full reload guarantees nothing in memory survives the
        // session change on a shared machine.
        queryClient.clear();
        window.location.assign("/");
      });
  };

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">LiquidTick</div>
        <nav className="tabs">
          {visible.map((t) => (
            <button
              key={t.id}
              className={`tabbtn${tab === t.id ? " active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {!t.ready && <span className="soon">soon</span>}
            </button>
          ))}
        </nav>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: mobile ? "auto" : undefined }}>
          <span className="topbar-user" style={{ fontSize: 12, color: "#8a93a6" }}>
            {identity.display_name}
            {identity.role === "operator" ? " · operator" : ""}
          </span>
          <button
            onClick={signOut}
            style={{
              fontSize: 12,
              padding: "4px 10px",
              borderRadius: 5,
              border: "1px solid rgba(255,255,255,0.14)",
              background: "transparent",
              color: "#cfe0ff",
              cursor: "pointer",
            }}
          >
            Sign out
          </button>
        </div>
      </header>
      <main className="content">
        {tab === "analytics" && <AnalyticsTab />}
        {tab === "governance" && <GovernanceTab identity={identity} />}
        {tab === "finance" && identity.role === "operator" && <FinanceTab identity={identity} />}
        {tab === "compliance" && <MonitorTab identity={identity} />}
      </main>
      {mobile && (
        <BottomNav accent="#4cc2ff"
          items={visible.map((t) => ({ id: t.id, label: t.label, icon: t.icon, active: tab === t.id, onClick: () => setTab(t.id) }))} />
      )}
    </div>
  );
}
